import type {
  DocumentValidationCaseResponse,
  DocumentValidationStartRequest,
  InAppNotification,
  PhoneTransferHistoryEntry,
  PhoneTransferStartRequest,
  PhoneTransferStartResponse,
  PhoneTransferVerifyRequest,
  PhoneTransferVerifyResponse,
  PhoneValidationResendRequest,
  PhoneValidationResendResponse,
  PhoneValidationStartRequest,
  PhoneValidationStartResponse,
  PhoneValidationVerifyRequest,
  PhoneValidationVerifyResponse,
  SurveyEligibilityResponse,
  SurveyValidationPolicyResponse,
  UpsertSurveyValidationPolicyRequest,
  ValidationProfileResponse,
} from "../types/validation.types";
import apiClient from "./api.service";

export class ValidationService {
  async getProfileValidation(): Promise<ValidationProfileResponse> {
    const response = await apiClient.get<{ data: ValidationProfileResponse }>(
      "/api/participant/profile/validation",
    );
    return response.data.data;
  }

  async getSurveyEligibility(surveyId: string): Promise<SurveyEligibilityResponse> {
    const response = await apiClient.get<{ data: SurveyEligibilityResponse }>(
      `/api/participant/surveys/${surveyId}/eligibility`,
    );

    const payload = response.data.data;
    return {
      ...payload,
      message:
        payload.message ||
        "Para participar desta pesquisa, voce precisa aumentar seu nivel de validacao.",
      cta: payload.cta || {
        label: "Aumentar nivel de validacao",
        action: "OPEN_VALIDATION_WIZARD",
      },
    };
  }

  async startPhoneValidation(
    payload: PhoneValidationStartRequest,
  ): Promise<PhoneValidationStartResponse> {
    const response = await apiClient.post<{ data: PhoneValidationStartResponse }>(
      "/api/participant/profile/validation/phone/start",
      payload,
    );

    return response.data.data;
  }

  async verifyPhoneValidation(
    payload: PhoneValidationVerifyRequest,
  ): Promise<PhoneValidationVerifyResponse> {
    const response = await apiClient.post<{ data: PhoneValidationVerifyResponse }>(
      "/api/participant/profile/validation/phone/verify",
      payload,
    );

    return response.data.data;
  }

  async resendPhoneValidation(
    payload: PhoneValidationResendRequest,
  ): Promise<PhoneValidationResendResponse> {
    const response = await apiClient.post<{ data: PhoneValidationResendResponse }>(
      "/api/participant/profile/validation/phone/resend",
      payload,
    );

    return response.data.data;
  }

  async getSurveyValidationPolicy(surveyId: string): Promise<SurveyValidationPolicyResponse> {
    const response = await apiClient.get<{ data: SurveyValidationPolicyResponse }>(
      `/api/contractor/surveys/${surveyId}/validation-policy`,
    );

    return response.data.data;
  }

  async upsertSurveyValidationPolicy(
    surveyId: string,
    payload: UpsertSurveyValidationPolicyRequest,
  ): Promise<SurveyValidationPolicyResponse> {
    const response = await apiClient.put<{ data: SurveyValidationPolicyResponse }>(
      `/api/contractor/surveys/${surveyId}/validation-policy`,
      payload,
    );

    return response.data.data;
  }

  async startDocumentValidation(
    payload: DocumentValidationStartRequest,
  ): Promise<DocumentValidationCaseResponse> {
    const response = await apiClient.post<{ data: DocumentValidationCaseResponse }>(
      '/api/participant/profile/validation/document/start',
      payload,
    );

    return response.data.data;
  }

  async getDocumentValidationStatus(caseId: number): Promise<DocumentValidationCaseResponse> {
    const response = await apiClient.get<{ data: DocumentValidationCaseResponse }>(
      `/api/participant/profile/validation/document/${caseId}`,
    );

    return response.data.data;
  }

  // Feature 015: Phone Transfer methods (T022)

  async startPhoneTransfer(
    payload: PhoneTransferStartRequest,
  ): Promise<PhoneTransferStartResponse> {
    const response = await apiClient.post<{ data: PhoneTransferStartResponse }>(
      "/api/participant/profile/validation/phone/transfer/start",
      payload,
    );
    return response.data.data;
  }

  async verifyPhoneTransfer(
    payload: PhoneTransferVerifyRequest,
  ): Promise<PhoneTransferVerifyResponse> {
    const response = await apiClient.post<{ data: PhoneTransferVerifyResponse }>(
      "/api/participant/profile/validation/phone/transfer/verify",
      payload,
    );
    return response.data.data;
  }

  // T027: Phone transfer history
  async getPhoneTransferHistory(): Promise<PhoneTransferHistoryEntry[]> {
    const response = await apiClient.get<{ data: PhoneTransferHistoryEntry[] }>(
      "/api/participant/profile/validation/phone/transfer/history",
    );
    return response.data.data;
  }

  // T030: In-app notifications
  async getNotifications(): Promise<InAppNotification[]> {
    const response = await apiClient.get<{ data: InAppNotification[] }>(
      "/api/participant/notifications",
    );
    return response.data.data;
  }
}

export const validationService = new ValidationService();
